import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import ConversationList from '../../components/ConversationList';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { db, storage } from '../../services/firebase';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { generateSignedPDF, uploadPDFToStorage } from '../../utils/pdfGenerator';

const ClientMessages = () => {
  const { currentUser } = useAuth();
  const { createEstimateNotification, getAdminUserIds } = useNotifications();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [submittingSignature, setSubmittingSignature] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showConversationList, setShowConversationList] = useState(true);
  const messagesEndRef = useRef(null);
  const unsubscribeRef = useRef(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setShowConversationList(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load conversations
  useEffect(() => {
    if (!currentUser) {
      console.log('💬 No current user, skipping conversation load');
      return;
    }

    console.log('💬 Loading conversations for user:', currentUser.uid);
    setLoading(true);

    const q = query(
      collection(db, 'estimates'),
      where('userId', '==', currentUser.uid),
      orderBy('updatedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('💬 Client messages snapshot received, size:', snapshot.size);
      const estimatesData = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('💬 Processing client estimate:', doc.id, 'userId:', data.userId, 'messages:', data.messages?.length || 0);
        return {
          id: doc.id,
          ...data
        };
      });
      
      console.log('💬 Client conversations set:', estimatesData.length);
      setConversations(estimatesData);
      setLoading(false);

      // Auto-select first conversation if none selected
      if (!selectedConversation && estimatesData.length > 0) {
        console.log('💬 Auto-selecting first conversation:', estimatesData[0].id);
        setSelectedConversation(estimatesData[0]);
      }
    }, (error) => {
      console.error('❌ Error loading client conversations:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Setup real-time listener for selected conversation
  useEffect(() => {
    if (!selectedConversation) return;

    // Clean up previous listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    const estimateRef = doc(db, 'estimates', selectedConversation.id);
    const unsubscribe = onSnapshot(estimateRef, (doc) => {
      if (doc.exists()) {
        const updatedEstimate = { id: doc.id, ...doc.data() };
        setSelectedConversation(updatedEstimate);
        
        // Update in conversations list
        setConversations(prev => 
          prev.map(conv => 
            conv.id === updatedEstimate.id ? updatedEstimate : conv
          )
        );

        // Mark admin messages as read
        markMessagesAsRead(updatedEstimate);
      }
    });

    unsubscribeRef.current = unsubscribe;

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [selectedConversation?.id]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [selectedConversation?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const markMessagesAsRead = async (conversation) => {
    if (!conversation.messages) return;

    const unreadAdminMessages = conversation.messages.filter(
      msg => msg.sender === 'admin' && !msg.read
    );

    if (unreadAdminMessages.length === 0) return;

    try {
      const updatedMessages = conversation.messages.map(msg => 
        msg.sender === 'admin' && !msg.read ? { ...msg, read: true } : msg
      );

      await updateDoc(doc(db, 'estimates', conversation.id), {
        messages: updatedMessages
      });
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // Create initial messages from estimate data if none exist
  const createInitialMessagesFromEstimate = async (estimate) => {
    if (estimate.messages && estimate.messages.length > 0) {
      return estimate; // Already has messages
    }

    const initialMessages = [];
    
    // Add description as initial message
    if (estimate.description && estimate.description.trim()) {
      initialMessages.push({
        id: Date.now().toString(),
        text: estimate.description.trim(),
        sender: 'client',
        senderName: currentUser.displayName || currentUser.email.split('@')[0],
        senderEmail: currentUser.email,
        timestamp: estimate.createdAt || new Date(),
        read: false,
        isInitialRequest: true
      });
    }

    // Add files as separate messages
    if (estimate.files && estimate.files.length > 0) {
      estimate.files.forEach((file, index) => {
        initialMessages.push({
          id: (Date.now() + index + 1).toString(),
          text: '', // No caption for initial files
          sender: 'client',
          senderName: currentUser.displayName || currentUser.email.split('@')[0],
          senderEmail: currentUser.email,
          timestamp: estimate.createdAt || new Date(),
          read: false,
          fileUrl: file.url,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          isInitialRequest: true
        });
      });
    }

    // Update the estimate with initial messages if any were created
    if (initialMessages.length > 0) {
      try {
        await updateDoc(doc(db, 'estimates', estimate.id), {
          messages: initialMessages,
          hasUnreadMessages: false, // Client doesn't need to see their own messages as unread
          updatedAt: new Date()
        });

        // Return updated estimate
        return {
          ...estimate,
          messages: initialMessages
        };
      } catch (error) {
        console.error('Error creating initial messages:', error);
        return estimate;
      }
    }

    return estimate;
  };

  const handleSelectConversation = async (conversation) => {
    // Create initial messages if they don't exist
    const updatedConversation = await createInitialMessagesFromEstimate(conversation);
    setSelectedConversation(updatedConversation);
    
    if (isMobile) {
      setShowConversationList(false);
    }
  };

  const handleBackToList = () => {
    setShowConversationList(true);
    setSelectedConversation(null);
  };

  const handleSendMessage = async (messageText) => {
    if (!selectedConversation || !messageText.trim() || sendingMessage) return;
    
    setSendingMessage(true);
    try {
      // Create user message object
      const userMessage = {
        id: Date.now().toString(),
        text: messageText.trim(),
        sender: 'client',
        senderName: currentUser.displayName || currentUser.email.split('@')[0],
        senderEmail: currentUser.email,
        timestamp: new Date(),
        read: false
      };
      
      // Save message to the database
      const currentMessages = selectedConversation.messages || [];
      const updatedMessages = [...currentMessages, userMessage];
      
      await updateDoc(doc(db, 'estimates', selectedConversation.id), {
        messages: updatedMessages,
        updatedAt: new Date(),
        hasUnreadMessages: true
      });
      
      // Notify admins
      try {
        const adminIds = await getAdminUserIds();
        if (adminIds.length > 0) {
          for (const adminId of adminIds) {
            await createEstimateNotification(
              selectedConversation,
              'message_received',
              adminId
            );
          }
        }
      } catch (error) {
        console.error('Error sending admin notification:', error);
      }
      
      showNotification('Message sent successfully!', 'success');
      
    } catch (error) {
      console.error('Error sending message:', error);
      showNotification('Failed to send message. Please try again.', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleSignatureSubmit = async (signatureData, signatureMessage) => {
    if (!signatureData || !signatureMessage || !selectedConversation) {
      console.error('Missing required data for signature submission');
      showNotification('Invalid signature data.', 'error');
      return;
    }

    setSubmittingSignature(true);
    try {
      console.log('Starting signature submission process...');
      
      // Generate signed PDF with signature
      console.log('Generating signed PDF...');
      console.log('Selected conversation data:', {
        id: selectedConversation.id,
        service: selectedConversation.service,
        userName: selectedConversation.userName,
        quoteAmount: selectedConversation.quoteAmount,
        hasQuoteNotes: !!selectedConversation.quoteNotes
      });
      
      const signedPdfBlob = await generateSignedPDF(
        selectedConversation,
        { 
          amount: selectedConversation.quoteAmount || 0, 
          notes: selectedConversation.quoteNotes || ''
        },
        signatureData
      );
      console.log('Signed PDF generated, blob size:', signedPdfBlob.size);
      
      // Upload signed PDF to Firebase Storage
      const userId = selectedConversation.userId || currentUser.uid;
      const signedPdfPath = `signatures/${userId}/${selectedConversation.id}/signed_estimate_${Date.now()}.pdf`;
      console.log('Uploading signed PDF to:', signedPdfPath);
      const signedPdfUrl = await uploadPDFToStorage(signedPdfBlob, signedPdfPath);
      console.log('Signed PDF uploaded successfully, URL:', signedPdfUrl);
      
      // Create signature completion message with safe data extraction
      const completionMessage = {
        id: Date.now().toString(),
        text: 'Document signed successfully! Thank you for your signature.',
        sender: 'system',
        senderName: 'System',
        senderEmail: 'system@greenoaktree.com',
        timestamp: new Date(),
        read: false,
        isSignatureCompletion: true,
        signatureData: signatureData,
        signedPdfUrl: signedPdfUrl,
        originalSignatureRequestId: signatureMessage.signatureRequestId,
        estimateData: {
          id: selectedConversation.id || '',
          service: selectedConversation.service || 'Tree Service',
          userName: selectedConversation.userName || currentUser.displayName || currentUser.email.split('@')[0],
          userEmail: selectedConversation.userEmail || currentUser.email,
          quoteAmount: selectedConversation.quoteAmount || 0,
          signedAt: new Date()
        }
      };

      // Update the original signature request message to mark as completed
      const currentMessages = selectedConversation.messages || [];
      const updatedMessages = currentMessages.map(msg => {
        if (msg.signatureRequestId === signatureMessage.signatureRequestId) {
          return {
            ...msg,
            signatureStatus: 'completed',
            signedAt: new Date(),
            signatureData: signatureData,
            signedPdfUrl: signedPdfUrl
          };
        }
        return msg;
      });

      // Add completion message
      updatedMessages.push(completionMessage);

      // Clean messages array to remove undefined values from nested objects
      const cleanMessages = updatedMessages.map(msg => {
        const cleanMsg = { ...msg };
        
        // Clean estimateData if it exists
        if (cleanMsg.estimateData) {
          Object.keys(cleanMsg.estimateData).forEach(key => {
            if (cleanMsg.estimateData[key] === undefined) {
              delete cleanMsg.estimateData[key];
            }
          });
        }
        
        // Remove any undefined top-level properties
        Object.keys(cleanMsg).forEach(key => {
          if (cleanMsg[key] === undefined) {
            delete cleanMsg[key];
          }
        });
        
        return cleanMsg;
      });

      // Prepare clean update data
      const cleanUpdateData = {
        messages: cleanMessages,
        signatureStatus: 'completed',
        status: 'approved',
        signedAt: new Date(),
        signatureData: signatureData,
        signedPdfUrl: signedPdfUrl,
        updatedAt: new Date(),
        hasUnreadMessages: true // Flag for admin to see signature completion
      };

      // Remove any undefined values from update data
      Object.keys(cleanUpdateData).forEach(key => {
        if (cleanUpdateData[key] === undefined) {
          delete cleanUpdateData[key];
        }
      });

      // Update estimate in Firestore with signature completion
      console.log('Updating Firestore with signature completion...');
      console.log('Clean update data:', cleanUpdateData);
      const estimateRef = doc(db, 'estimates', selectedConversation.id);
      await updateDoc(estimateRef, cleanUpdateData);
      console.log('Firestore update completed successfully');

      // Notify admins about signature completion
      try {
        const adminUserIds = await getAdminUserIds();
        console.log('💬 AdminIds for signature notification:', adminUserIds);
        
        if (adminUserIds && adminUserIds.length > 0) {
          for (const adminId of adminUserIds) {
            console.log('💬 Sending signature notification to admin:', adminId);
            await createEstimateNotification(
              selectedConversation,
              'signature_completed',
              adminId
            );
          }
          console.log('💬 Successfully sent signature notifications to all admins');
        } else {
          console.error('💬 No admin IDs found for signature notification');
        }
      } catch (notificationError) {
        console.error('Error sending signature completion notifications to admins:', notificationError);
      }

      console.log('Signature submission completed successfully');
      showNotification('Document signed successfully! Your estimate has been approved.', 'success');

    } catch (error) {
      console.error('Error submitting signature:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      showNotification('Failed to submit signature. Please try again.', 'error');
    } finally {
      console.log('Setting submittingSignature to false');
      setSubmittingSignature(false);
    }
  };

  // Mobile layout: show either conversation list or chat
  if (isMobile) {
    return (
      <Layout>
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
          {/* Mobile Header */}
          <div style={{
            background: 'linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)',
            color: 'white',
            padding: window.innerWidth <= 480 ? '0.75rem' : '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: window.innerWidth <= 480 ? '0.75rem' : '1rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            minHeight: '60px'
          }}>
            {!showConversationList && (
              <button
                onClick={handleBackToList}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  color: 'white',
                  fontSize: window.innerWidth <= 480 ? '1rem' : '1.2rem',
                  cursor: 'pointer',
                  padding: window.innerWidth <= 480 ? '0.5rem' : '0.75rem',
                  borderRadius: '50%',
                  minWidth: '40px',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
              >
                ←
              </button>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ 
                margin: 0, 
                fontSize: window.innerWidth <= 480 ? '1.125rem' : '1.2rem', 
                fontWeight: '600',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {showConversationList ? 'Messages' : selectedConversation?.service || 'Chat'}
              </h1>
              {!showConversationList && selectedConversation && (
                <p style={{
                  margin: '0.25rem 0 0 0',
                  fontSize: window.innerWidth <= 480 ? '0.75rem' : '0.8rem',
                  opacity: 0.9,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  Status: {selectedConversation.status?.toUpperCase() || 'PENDING'}
                </p>
              )}
            </div>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {showConversationList ? (
              <ConversationList
                conversations={conversations}
                selectedConversation={selectedConversation}
                onSelectConversation={handleSelectConversation}
                currentUserType="client"
                loading={loading}
              />
            ) : (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Messages Display */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', padding: '1rem', overflowY: 'auto' }}>
                  {selectedConversation?.messages?.map((message, index) => (
                    <div 
                      key={message.id || index}
                      style={{
                        display: 'flex',
                        justifyContent: message.sender === 'client' ? 'flex-end' : 'flex-start',
                        marginBottom: '0.75rem'
                      }}
                    >
                      <div
                        style={{
                          maxWidth: '70%',
                          padding: '0.875rem 1rem',
                          borderRadius: message.sender === 'client' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          backgroundColor: message.sender === 'client' ? '#2e7d32' : '#f8f9fa',
                          color: message.sender === 'client' ? 'white' : '#333',
                          border: message.sender !== 'client' ? '1px solid #e0e0e0' : 'none',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                        }}
                      >
                        <div style={{ fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '0.375rem' }}>
                          {message.text}
                        </div>
                        <div style={{
                          fontSize: '0.7rem',
                          opacity: 0.75,
                          textAlign: message.sender === 'client' ? 'right' : 'left',
                          marginTop: '0.375rem'
                        }}>
                          {message.sender === 'client' ? 'You' : 'Support Team'} • {
                            new Date(message.timestamp?.toDate ? message.timestamp.toDate() : message.timestamp).toLocaleString()
                          }
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                
                {/* Message Input */}
                <div style={{ padding: '1rem', borderTop: '1px solid #e0e0e0', background: 'white' }}>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <input 
                      type="text" 
                      placeholder="Type your message..."
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        outline: 'none'
                      }}
                      value={message || ''}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(message);
                          setMessage('');
                        }
                      }}
                      disabled={sendingMessage}
                    />
                    <button
                      onClick={() => {
                        handleSendMessage(message);
                        setMessage('');
                      }}
                      disabled={!message?.trim() || sendingMessage}
                      style={{
                        background: (!message?.trim() || sendingMessage) ? '#ccc' : '#2e7d32',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0 1rem',
                        cursor: (!message?.trim() || sendingMessage) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {sendingMessage ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            background: notification.type === 'success' ? '#d4edda' : '#f8d7da',
            color: notification.type === 'success' ? '#155724' : '#721c24',
            padding: '1rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            maxWidth: '300px'
          }}>
            {notification.message}
          </div>
        )}
      </Layout>
    );
  }

  // Desktop layout: side-by-side
  return (
    <Layout>
      <div style={{ 
        height: 'calc(100vh - 80px)', // Subtract header height
        display: 'flex', 
        flexDirection: 'column',
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)',
          color: 'white',
          padding: isMobile ? '1rem' : '1.5rem 2rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          flexShrink: 0
        }}>
          <h1 style={{ 
            margin: 0, 
            fontSize: window.innerWidth <= 1024 ? '1.375rem' : '1.5rem', 
            fontWeight: '600' 
          }}>
            Messages
          </h1>
          <p style={{ 
            margin: '0.5rem 0 0 0', 
            opacity: 0.9, 
            fontSize: window.innerWidth <= 1024 ? '0.85rem' : '0.9rem' 
          }}>
            Chat with our team about your estimates
          </p>
        </div>

        {/* Main Content */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          overflow: 'hidden',
          minHeight: 0 // Important for flexbox overflow
        }}>
          {/* Conversation List */}
          <div style={{
            width: isMobile ? '100%' : '350px',
            minWidth: isMobile ? '100%' : '300px',
            borderRight: isMobile ? 'none' : '1px solid #e0e0e0',
            background: 'white',
            display: showConversationList || !isMobile ? 'flex' : 'none',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '1rem',
              borderBottom: '1px solid #f0f0f0',
              background: '#f8f9fa'
            }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#333' }}>
                Your Conversations ({conversations.length})
              </h3>
            </div>
            
            <div style={{ 
              flex: 1, 
              overflow: 'auto',
              minHeight: 0 // Important for flexbox overflow
            }}>
              <ConversationList
                conversations={conversations}
                selectedConversation={selectedConversation}
                onSelectConversation={handleSelectConversation}
                currentUserType="client"
                loading={loading}
              />
            </div>
          </div>

          {/* AI Assistant */}
          <div style={{ 
            flex: 1, 
            display: (showConversationList || !isMobile) ? 'flex' : 'none',
            flexDirection: 'column', 
            background: '#f8f9fa',
            minHeight: 0 // Important for flexbox overflow
          }}>
            {selectedConversation ? (
              <>
                {/* Messages Display */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', padding: '1rem', overflowY: 'auto' }}>
                  {selectedConversation?.messages?.map((message, index) => (
                    <div 
                      key={message.id || index}
                      style={{
                        display: 'flex',
                        justifyContent: message.sender === 'client' ? 'flex-end' : 'flex-start',
                        marginBottom: '0.75rem'
                      }}
                    >
                      <div
                        style={{
                          maxWidth: '70%',
                          padding: '0.875rem 1rem',
                          borderRadius: message.sender === 'client' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          backgroundColor: message.sender === 'client' ? '#2e7d32' : '#f8f9fa',
                          color: message.sender === 'client' ? 'white' : '#333',
                          border: message.sender !== 'client' ? '1px solid #e0e0e0' : 'none',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                        }}
                      >
                        <div style={{ fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '0.375rem' }}>
                          {message.text}
                        </div>
                        <div style={{
                          fontSize: '0.7rem',
                          opacity: 0.75,
                          textAlign: message.sender === 'client' ? 'right' : 'left',
                          marginTop: '0.375rem'
                        }}>
                          {message.sender === 'client' ? 'You' : 'Support Team'} • {
                            new Date(message.timestamp?.toDate ? message.timestamp.toDate() : message.timestamp).toLocaleString()
                          }
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                
                {/* Message Input */}
                <div style={{ padding: '1rem', borderTop: '1px solid #e0e0e0', background: 'white' }}>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <input 
                      type="text" 
                      placeholder="Type your message..."
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        outline: 'none'
                      }}
                      value={message || ''}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(message);
                          setMessage('');
                        }
                      }}
                      disabled={sendingMessage}
                    />
                    <button
                      onClick={() => {
                        handleSendMessage(message);
                        setMessage('');
                      }}
                      disabled={!message?.trim() || sendingMessage}
                      style={{
                        background: (!message?.trim() || sendingMessage) ? '#ccc' : '#2e7d32',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0 1rem',
                        cursor: (!message?.trim() || sendingMessage) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {sendingMessage ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                color: '#666',
                padding: '2rem'
              }}>
                <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>💬</div>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>Select a conversation</h3>
                <p style={{ margin: 0, textAlign: 'center' }}>
                  Choose a conversation from the left to start chatting
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          background: notification.type === 'success' ? '#d4edda' : '#f8d7da',
          color: notification.type === 'success' ? '#155724' : '#721c24',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          maxWidth: '400px'
        }}>
          {notification.message}
        </div>
      )}
    </Layout>
  );
};

// Helper function to get status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending': return '#ffc107';
    case 'quoted': return '#17a2b8';
    case 'approved': return '#28a745';
    case 'declined': return '#dc3545';
    case 'completed': return '#6f42c1';
    default: return '#6c757d';
  }
};

export default ClientMessages;
